import { Image } from '~/ui/image';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router';
import { Text } from '@understory-io/leaf/text';
import { VisuallyHidden } from '@understory-io/leaf/visually-hidden';
import { cx } from '~/styled-system/css';
import { css } from '@understory-io/leaf/css';
import type { HeaderConfig } from '~/lib/header-config';
import { focusVisibleRing } from './shared';
import { HeaderMenu, type HeaderMenuFeatures } from './header-menu';
import { LanguageSwitcher } from './language-switcher';
import { LinkButton } from '@understory-io/leaf/link-button';
import { TextLink } from '~/ui/link';
import { ArrowLeft } from '../icons/arrow-left';
import { CloseIcon } from '../icons/close';
import { GiftCardIcon } from '../icons/gift-card';
import { localeHref } from '~/lib/use-locale-href';
import { trackHeaderCompanyWebsiteClicked } from '~/domain/plugins/services/track-header-website-click.client';

type HeaderProps = {
  companyName?: string;
  companyWebsite?: string;
  logo?: string;
  config?: HeaderConfig;
  features?: HeaderMenuFeatures;
  /** Path-param locale (only set when URL is non-default-language prefixed). */
  locale?: string;
  /** Languages the storefront publishes. The language switcher renders only
   *  when there's more than one. */
  availableLanguages?: string[];
  /** Server-resolved canonical locale (e.g. `en-GB`) — selects the flag and
   *  the highlighted option in the language switcher. */
  resolvedLocale?: string;
  /** Storefront's default language — drives whether a switch adds a locale
   *  prefix to the URL. */
  defaultLanguage?: string;
};

export function Header({
  companyName,
  companyWebsite,
  logo,
  config,
  features,
  locale,
  availableLanguages,
  resolvedLocale,
  defaultLanguage,
}: HeaderProps) {
  const intl = useIntl();
  // The logo link wraps an image with `alt=""` (decorative when paired with a
  // company name fallback). Without an explicit accessible name on the link,
  // axe flags it under the `link-name` rule. Fall back to a localized
  // "Home" when no company name is supplied.
  const logoLinkLabel =
    companyName ??
    intl.formatMessage({
      defaultMessage: 'Home',
      description:
        'Accessible label for the header logo link when no company name is available',
    });

  // A viewBox-only SVG (no intrinsic width/height) has no intrinsic pixel size,
  // so `width: auto` + `height: auto` both resolve to 0 and the logo renders as
  // a blank 0×0 box. Give SVG logos a definite height to scale from; width
  // stays auto (derived from the viewBox aspect ratio) and is still capped by
  // max-width. Raster logos keep today's intrinsic-size behaviour.
  const isSvgLogo = !!logo && /\.svg(\?|$)/i.test(logo);
  return (
    <header
      className={css({
        paddingBlock: { base: 'small', tablet: 'medium' },
        paddingInline: { base: 'small', tablet: '2xlarge' },
      })}
    >
      <div
        className={css({
          // Full-bleed: header content spans the whole viewport width (no
          // breakpoint-desktop max-width cap), unlike the page body.
          display: { base: 'flex', tablet: 'grid' },
          justifyContent: 'space-between',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          height: { base: '2xlarge', tablet: '4xlarge' },
        })}
      >
        <div className={css({ justifySelf: 'start' })}>
          {config?.backLink && (
            <TextLink
              to={config.backLink}
              onClick={config.onBackClick}
              color="secondary"
              underline={false}
              className={css({ display: 'inline-flex', alignItems: 'center' })}
            >
              {config.backIcon === 'close' ? (
                <CloseIcon size="medium" />
              ) : (
                <ArrowLeft size="medium" />
              )}
              <VisuallyHidden>
                {config.backLabel ?? (
                  <FormattedMessage
                    defaultMessage="Back"
                    description="Aria label for the header back button"
                  />
                )}
              </VisuallyHidden>
            </TextLink>
          )}
        </div>

        {/** TODO: remove the reloadDocument here when home is migrated to vnext app */}
        <Link
          to={companyWebsite ?? '/'}
          reloadDocument
          // For most companies this link leaves the storefront for their own
          // website — from any page, checkout included — and until now nothing
          // recorded it, while the checkout header's cross fired
          // `Checkout Dismissed`. The handler never preventDefaults: the
          // navigation is the point, the event is a side note.
          onClick={() =>
            trackHeaderCompanyWebsiteClicked(companyWebsite ?? '/')
          }
          aria-label={logoLinkLabel}
          className={cx(
            focusVisibleRing,
            css({
              display: { base: 'none', tablet: 'flex' },
              alignItems: 'center',
              justifyContent: 'center',
              textDecoration: 'none',
              borderRadius: 'small',
            })
          )}
        >
          {logo ? (
            <Image
              src={logo}
              alt={''}
              layout="constrained"
              width={256}
              height={80}
              // Unpic's inline constrained styles would beat the class below:
              // they cap the logo at 80px tall and force the 256x80 aspect
              // ratio on logos of any shape. We opt out of them (`unstyled`)
              // and apply the 40px cap via a Panda class instead. `layout` and
              // `width`/`height` stay so Unpic still builds the srcset.
              unstyled
              className={css({
                maxWidth: '[256px]',
                maxHeight: '[40px]',
                width: '[auto]',
                height: isSvgLogo ? '[40px]' : '[auto]',
                objectFit: 'contain',
              })}
            />
          ) : (
            <Text style="headline">{companyName}</Text>
          )}
        </Link>

        <div
          className={css({
            justifySelf: 'end',
            display: 'flex',
            alignItems: 'center',
            gap: 'small',
          })}
        >
          {availableLanguages && resolvedLocale && (
            <LanguageSwitcher
              availableLanguages={availableLanguages}
              currentLocale={resolvedLocale}
              defaultLanguage={defaultLanguage}
            />
          )}
          {features?.giftCards && (
            <LinkButton
              appearance="text"
              href={localeHref(locale, '/:locale?/giftcards')}
              className={css({
                color: 'content.secondary',
                height: 'large',
                padding: 'micro',
              })}
              aria-label={intl.formatMessage({
                defaultMessage: 'Buy a gift card',
                description:
                  'Accessible label for the gift card icon button in the header',
              })}
            >
              <GiftCardIcon size="large-inset" />
            </LinkButton>
          )}
          <HeaderMenu features={features} locale={locale} />
        </div>
      </div>
    </header>
  );
}
